"use client";

import {
  Box,
  Button,
  Flex,
  HStack,
  Icon,
  Menu,
  Portal,
  Text,
  useTabs,
} from "@chakra-ui/react";
import { useCallback, useState } from "react";
import { useTranslations } from "next-intl";
import { FaDownload } from "react-icons/fa6";
import { HiSparkles } from "react-icons/hi";
import { LuChevronDown, LuFileText } from "react-icons/lu";
import useSWR from "swr";
import { FullViewResume } from "@/_models/resume";
import { useAuthStore } from "@/_stores/useAuthStore";
import { atsResumeService } from "@/_services/resumeServices/ats-resume";
import { useGenerateATSResume } from "@/_components/ui/ResumeUI/_hooks/useGenerateATSResume";
import { GenerateATSResumeDialog } from "@/_components/ui/ResumeUI/DetailModal/GenerateATSResumeDialog";
import { HorizontalTabs, TabItem } from "@/_components/ui/HorizontalTabs";
import { Emoji } from "@/_components/ui/Emoji/Emoji";
import { AnimatedDownloadButton } from "@/_components/ui/AnimatedDownloadButton/AnimatedDownloadButton";
import { ResumeLock } from "./ResumeLock";
import { DigitalVersionContent } from "./DigitalVersionContent";
import { ATSVersionContent } from "./ATSVersionContent";
import {
  ActiveResumeView,
  RESUME_DETAIL_RESUME_NS,
  ResumeVersion,
  downloadPdf,
} from "./constants";
import { useThemeColorScope } from "@/_theme/useThemeColorScope";

const outlineButtonProps = {
  visual: "outline" as const,
  color: "themeColor.fg.body",
  borderColor: "themeColor.border.default",
  bg: "transparent",
  _hover: {
    bg: "themeColor.sub.hoverBg",
    borderColor: "themeColor.sub.borderHover",
  },
};

// ─── ResumeInfo (tabbed, used in modal/full-page view) ───────────────────────

export interface ResumeInfoProps {
  fullResume?: FullViewResume;
  onClickUnlock: () => void;
  isLoading?: boolean;
  height?: string;
  width?: string;
  isInModal?: boolean;
}

export function ResumeInfo({
  fullResume,
  onClickUnlock,
  isLoading = false,
  isInModal = false,
}: ResumeInfoProps) {
  const t = useTranslations(RESUME_DETAIL_RESUME_NS);

  const versionTabs: TabItem[] = [
    {
      id: ResumeVersion.original,
      icon: () => <Emoji emoji="page-with-curl" size={5} />,
      label: t("field.original_version"),
      content: (
        <ResumeLock
          fullResume={fullResume}
          onClickUnlock={onClickUnlock}
          isLoading={isLoading}
        />
      ),
    },
    {
      id: ResumeVersion.digital,
      icon: () => <Emoji emoji="sparkles" size={5} />,
      label: t("field.digital_version"),
      content: (
        <DigitalVersionContent
          fullResume={fullResume}
          onClickUnlock={onClickUnlock}
          isLoading={isLoading}
        />
      ),
    },
  ];

  const tabContext = useTabs({ defaultValue: ResumeVersion.original });

  return (
    <Box
      w="100%"
      h="fit-content"
      display="flex"
      flexDirection="column"
      position="relative"
      id="resume-info-container"
    >
      <HorizontalTabs
        tabs={versionTabs}
        onTabChange={() => {}}
        initialValue={ResumeVersion.original}
        showContent={true}
        fullWidthContent={true}
        centerTabs={true}
        outerModalHeight={
          isInModal ? "calc(100vh - 20rem)" : "calc(100vh - 14rem)"
        }
        tabContext={tabContext}
      />
      {fullResume?.download_url && (
        <Box position="absolute" top={1} right={4}>
          <AnimatedDownloadButton
            downloadUrl={fullResume.download_url}
            onDownload={async () => {
              downloadPdf(
                fullResume.download_url,
                fullResume.resume.job_details.basic_info.name,
              );
            }}
          />
        </Box>
      )}
    </Box>
  );
}
export const getATSApiKey = (resumeId: string) => `/ats/by-resume/${resumeId}`;

// ─── ResumeInfoSimple (dropdown switcher, used in side-panel / drawer) ────────

export interface ResumeInfoSimpleProps {
  fullResume?: FullViewResume;
  onClickUnlock: () => void;
  isLoading?: boolean;
  height?: string;
  width?: string;
}

export function ResumeInfoSimple({
  fullResume,
  onClickUnlock,
  isLoading = false,
  height,
}: ResumeInfoSimpleProps) {
  const t = useTranslations(RESUME_DETAIL_RESUME_NS);
  const themeColorScope = useThemeColorScope();
  const token = useAuthStore((s) => s.token);
  const resumeId = fullResume?.resume.id;
  const fileBasename =
    fullResume?.resume.job_details.basic_info.name ?? "resume";

  const [activeView, setActiveView] = useState<ActiveResumeView>("original");

  const {
    data: atsData,
    isLoading: isLoadingAts,
    mutate: refetchAts,
  } = useSWR(
    token && resumeId ? getATSApiKey(resumeId) : null,
    () => atsResumeService.getATSByResumeId(resumeId!),
    {
      revalidateOnFocus: false,
      shouldRetryOnError: false,
    },
  );

  const handleATSSuccess = useCallback(() => {
    void refetchAts();
    setActiveView("ats");
  }, [refetchAts]);

  const {
    isDialogOpen,
    jobStatus,
    progress,
    activeStepIndex,
    isGenerating,
    startGenerate,
    closeDialog,
    retryGenerate,
  } = useGenerateATSResume({
    resumeId,
    fileBasename,
    onSuccess: handleATSSuccess,
  });

  const currentDownloadUrl =
    activeView === "original" ? fullResume?.download_url : atsData?.file_url;
  const currentDownloadName =
    activeView === "original" ? fileBasename : `${fileBasename}-ats`;

  return (
    <Flex
      w="full"
      h={height ?? "full"}
      minH={0}
      flexDirection="column"
      position="relative"
      id="resume-info-simple-container"
      overflow="hidden"
      pb={1}
      rounded="xl"
      bg="themeColor.bg.heading"
      border="0.5px solid"
      borderColor="themeColor.border.default"
    >
      {fullResume?.download_url && (
        <HStack
          flexShrink={0}
          px={2}
          zIndex={10}
          py={2}
          w="full"
          justify="space-between"
          bg="themeColor.bg.heading"
          roundedTop="xl"
        >
          <HStack gap={1.5} p={4}>
            <Box
              rounded="full"
              flexShrink={0}
              boxSize={2}
              bg={
                activeView === "original"
                  ? "themeColor.fg.muted"
                  : "themeColor.fg.brandStrong"
              }
            />
            <Text fontWeight="medium" fontSize="sm" color="themeColor.fg.body">
              {activeView === "original"
                ? t("field.cv_original")
                : t("field.cv_ats")}
            </Text>
            <Box
              w="fit-content"
              h="full"
              bg="themeColor.border.default"
              px={1.5}
              py={0.5}
              rounded="lg"
            >
              <Text fontSize="2xs" color="themeColor.fg.body" fontWeight="bold">
                {activeView === "original" ? "PDF" : "ATS Standard"}
              </Text>
            </Box>
          </HStack>

          <HStack gap={0}>
            <Button
              {...outlineButtonProps}
              size="sm"
              roundedRight="none"
              borderRightWidth={0}
              disabled={!currentDownloadUrl}
              onClick={async () => {
                if (currentDownloadUrl) {
                  await downloadPdf(currentDownloadUrl, currentDownloadName);
                }
              }}
              _hover={{ ...outlineButtonProps._hover, px: "4" }}
              transition="all 0.2s ease-in-out"
            >
              <Icon as={FaDownload} />
              <Text as="span" fontSize="xs" color="inherit">
                {t("field.download")}
              </Text>
            </Button>
            <Menu.Root positioning={{ strategy: "fixed" }}>
              <Menu.Trigger asChild>
                <Button
                  {...outlineButtonProps}
                  size="sm"
                  roundedLeft="none"
                  px={2}
                  minW={0}
                >
                  <Icon as={LuChevronDown} />
                </Button>
              </Menu.Trigger>
              <Portal>
                <Menu.Positioner>
                  <Menu.Content
                    {...themeColorScope}
                    minW="180px"
                    bg="themeColor.form.menuBg"
                    border="1px solid"
                    borderColor="themeColor.form.menuBorder"
                    css={{
                      "--menu-bg": "colors.themeColor.form.menuBg",
                    }}
                    rounded="2xl"
                  >
                    <Menu.Item
                      value="original"
                      onClick={() => setActiveView("original")}
                      fontWeight={activeView === "original" ? "bold" : "normal"}
                      rounded="xl"
                    >
                      <HStack w="full" justify="space-between">
                        <HStack
                          gap={2}
                          color={
                            activeView === "original"
                              ? "themeColor.fg.brandStrong"
                              : "themeColor.fg.muted"
                          }
                        >
                          <Icon as={LuFileText} color="inherit" />
                          <Text fontSize="sm" color="inherit">
                            {t("field.cv_original")}
                          </Text>
                        </HStack>
                        <Box
                          w="fit-content"
                          h="full"
                          bg="themeColor.border.default"
                          px={1.5}
                          py={0.5}
                          rounded="lg"
                        >
                          <Text
                            fontSize="2xs"
                            color="themeColor.fg.body"
                            fontWeight="bold"
                          >
                            PDF
                          </Text>
                        </Box>
                      </HStack>
                    </Menu.Item>
                    <Menu.Item
                      value="ats"
                      onClick={() => setActiveView("ats")}
                      fontWeight={activeView === "ats" ? "semibold" : "normal"}
                      rounded="xl"
                    >
                      <HStack w="full" justify="space-between">
                        <HStack
                          gap={2}
                          color={
                            activeView === "ats"
                              ? "themeColor.fg.brandStrong"
                              : "themeColor.fg.muted"
                          }
                        >
                          <Icon as={HiSparkles} color="inherit" />
                          <Text fontSize="sm" color="inherit">
                            {t("field.cv_ats")}
                          </Text>
                        </HStack>
                        <Box
                          w="fit-content"
                          h="full"
                          bg="themeColor.border.default"
                          px={1.5}
                          py={0.5}
                          rounded="lg"
                        >
                          <Text
                            fontSize="2xs"
                            color="themeColor.fg.body"
                            fontWeight="bold"
                          >
                            ATS
                          </Text>
                        </Box>
                      </HStack>
                    </Menu.Item>
                  </Menu.Content>
                </Menu.Positioner>
              </Portal>
            </Menu.Root>
          </HStack>
        </HStack>
      )}

      <GenerateATSResumeDialog
        open={isDialogOpen}
        status={jobStatus}
        progress={progress}
        activeStepIndex={activeStepIndex}
        onCancel={closeDialog}
        onRetry={() => void retryGenerate()}
      />

      <Box id="preview-resume" flex="1" minH={0} overflow="auto" mx={1}>
        {activeView === "ats" && fullResume?.download_url ? (
          <ATSVersionContent
            atsFileUrl={atsData?.file_url}
            isLoading={isLoadingAts}
            onGenerate={() => void startGenerate()}
            isGenerating={isGenerating}
          />
        ) : (
          <ResumeLock
            fullResume={fullResume}
            onClickUnlock={onClickUnlock}
            isLoading={isLoading}
          />
        )}
      </Box>
    </Flex>
  );
}
